// src/pages/Library.js
// ──────────────────────────────────────────────────────────────────────────
// MTI Library / Learning Resource Centre — password-gated, premium catalogue.
// Books have designed covers, are pressable, and open their PDF in a viewer.
//
// PDFs: drop your files in  public/library/<slug>.pdf  (the slug is derived
// from each title — e.g. "Crop production handbook" → crop-production-handbook.pdf).
// Until a file exists the viewer will show the browser's "not found".
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { supabaseReady } from '../lib/supabase';
import { fetchLibrary, signLibraryFile } from '../lib/cms';
import { BUILT_IN_LIBRARY, DEPT_COLOR, LIBRARY_DEPTS, librarySlug } from '../api/library';
import theme from '../theme/theme';

// PDFs live in a PRIVATE Supabase Storage bucket called "library". Members get
// a signed URL valid for 10 minutes, so files can't be fetched by guessing an
// address. Which object belongs to a title is recorded in library.file_path
// rather than derived from the title, so renaming a book no longer breaks its
// file. If Supabase isn't configured we fall back to public/library/<slug>.pdf
// for local testing.
const publicFileFor = (item) => `/library/${item.file_path || `${librarySlug(item.title)}.pdf`}`;

async function getFileUrl(item) {
  if (!supabaseReady) return publicFileFor(item);
  const path = item.file_path || `${librarySlug(item.title)}.pdf`;
  const signed = await signLibraryFile(path);
  return signed || publicFileFor(item);
}

// ── Shared ──────────────────────────────────────────────────
const Header = styled.header`
  background: ${theme.color.blueDeep}; color: #fff;
  padding: 7rem ${theme.layout.gutter} 5rem;
  .inner { max-width: ${theme.layout.maxWidth}; margin: 0 auto; }
  h1 { font-weight: 400; font-size: clamp(2.4rem, 6vw, 4rem); color: #fff; margin-top: 1.2rem; }
  p { margin-top: 1rem; max-width: 54ch; font-weight: 300; color: rgba(255,255,255,0.82); }
  .kicker { color: #fff; }
`;
const Section = styled.section`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 4.5rem ${theme.layout.gutter};
`;

// ── Covers ──────────────────────────────────────────────────
const Cover = styled.div`
  position: relative; border-radius: 5px; overflow: hidden; background: ${(p) => p.$bg};
  color: #fff; display: flex; flex-direction: column; justify-content: flex-end;
  box-shadow: 0 14px 30px -16px rgba(11,42,99,0.55);
  &::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 7px; background: rgba(0,0,0,0.30); }
  &::after { content: ''; position: absolute; left: 7px; top: 0; bottom: 0; width: 2px; background: ${theme.color.lemon}; opacity: 0.9; }
`;

// ── Catalogue ───────────────────────────────────────────────
const Controls = styled.div`
  display: flex; gap: 0.75rem; flex-wrap: wrap; align-items: center; margin-bottom: 1.4rem;
  input { flex: 1 1 220px; font-family: ${theme.font.body}; font-size: 0.98rem; padding: 12px 16px;
          border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; background: #fff; color: ${theme.color.ink}; }
  input:focus { outline: none; border-color: ${theme.color.accent}; }
`;
const Toggle = styled.div`
  margin-left: auto; display: flex; border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; overflow: hidden;
  button { background: #fff; border: none; cursor: pointer; padding: 10px 14px; font-size: 0.85rem; font-weight: 600; color: ${theme.color.inkSoft}; }
  button.active { background: ${theme.color.accent}; color: #fff; }
`;
const Chips = styled.div`
  display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1.6rem;
  button { background: #fff; border: 1px solid ${theme.color.line}; border-radius: 100px; padding: 7px 16px;
           font-family: ${theme.font.body}; font-size: 0.82rem; font-weight: 600; color: ${theme.color.inkSoft}; cursor: pointer; }
  button.active { background: ${theme.color.accent}; color: #fff; border-color: ${theme.color.accent}; }
`;
const Count = styled.p` font-size: 0.85rem; color: ${theme.color.inkSoft}; margin: 0 0 1.6rem; `;

const Tiles = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(168px, 1fr)); gap: 1.7rem;
`;
const Tile = styled.button`
  text-align: left; background: none; border: none; padding: 0; cursor: pointer; font-family: inherit;
  .cover { aspect-ratio: 3/4; padding: 16px 14px 16px 22px; transition: transform 0.3s ${theme.ease}, box-shadow 0.3s ${theme.ease}; }
  .cover .type { font-size: 0.62rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: ${theme.color.lemon}; }
  .cover .title { font-family: ${theme.font.display}; font-weight: 600; font-size: 1.05rem; line-height: 1.14; margin-top: 8px; }
  .cover .dept { font-size: 0.66rem; opacity: 0.85; margin-top: 10px; }
  .cover .open { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
                 background: rgba(8,24,60,0.55); color: #fff; font-size: 0.82rem; font-weight: 700; letter-spacing: 0.04em;
                 opacity: 0; transition: opacity 0.25s ${theme.ease}; }
  &:hover .cover { transform: translateY(-6px); box-shadow: 0 22px 44px -18px rgba(11,42,99,0.6); }
  &:hover .cover .open { opacity: 1; }
  .meta { margin-top: 0.7rem; font-size: 0.8rem; color: ${theme.color.inkSoft}; display: flex; align-items: center; gap: 6px; }
`;

const Rows = styled.div` border-top: 1px solid ${theme.color.line}; `;
const Row = styled.button`
  width: 100%; text-align: left; background: none; border: none; font-family: inherit; cursor: pointer;
  display: flex; align-items: center; gap: 1rem; padding: 0.9rem 0; border-bottom: 1px solid ${theme.color.line};
  .mini { width: 44px; height: 60px; flex: none; padding: 6px 5px 6px 10px; justify-content: flex-start; }
  .mini .i { font-family: ${theme.font.display}; font-weight: 700; font-size: 0.9rem; }
  .t { font-weight: 500; color: ${theme.color.ink}; flex: 1; font-size: 1.02rem; }
  .type { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: ${theme.color.accent}; min-width: 90px; }
  .dept { font-size: 0.74rem; color: ${theme.color.inkSoft}; white-space: nowrap; min-width: 96px; text-align: right; }
  &:hover .t { color: ${theme.color.accent}; }
`;
const Empty = styled.p` color: ${theme.color.inkSoft}; padding: 2.5rem 0; `;

// ── PDF viewer modal ────────────────────────────────────────
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1000; background: rgba(8,24,60,0.6);
  display: flex; align-items: center; justify-content: center; padding: 3vh 3vw;
`;
const Modal = styled.div`
  width: min(960px, 100%); max-height: 94vh; background: #fff; border-radius: ${theme.layout.radius};
  overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 40px 80px -30px rgba(0,0,0,0.6);
  .bar { display: flex; align-items: center; gap: 1rem; padding: 1rem 1.3rem; border-bottom: 1px solid ${theme.color.line}; }
  .bar .meta { flex: 1; min-width: 0; }
  .bar .t { font-family: ${theme.font.display}; font-weight: 600; font-size: 1.15rem; color: ${theme.color.ink}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .bar .s { font-size: 0.78rem; color: ${theme.color.inkSoft}; }
  .bar a, .bar button { font-family: ${theme.font.body}; font-size: 0.82rem; font-weight: 600; border-radius: ${theme.layout.radius}; padding: 8px 14px; cursor: pointer; white-space: nowrap; }
  .bar a { background: ${theme.color.accent}; color: #fff; border: none; }
  .bar .close { background: #fff; border: 1px solid ${theme.color.line}; color: ${theme.color.ink}; }
  iframe { width: 100%; height: 74vh; border: none; background: ${theme.color.paperAlt}; }
`;

function Viewer({ item, onClose }) {
  const [file, setFile] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    if (!item) return undefined;
    setLoading(true); setFile('');
    getFileUrl(item).then((url) => { if (active) { setFile(url); setLoading(false); } });
    return () => { active = false; };
  }, [item]);

  if (!item) return null;
  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <div className="bar">
          <div className="meta">
            <div className="t">{item.title}</div>
            <div className="s">{item.department} · {item.type} · PDF</div>
          </div>
          {file && <a href={file} target="_blank" rel="noreferrer">Open in new tab ↗</a>}
          <button className="close" onClick={onClose}>Close</button>
        </div>
        {loading
          ? <div style={{ padding: '4rem', textAlign: 'center', color: theme.color.inkSoft }}>Loading document…</div>
          : <iframe title={item.title} src={file} />}
      </Modal>
    </Overlay>
  );
}

// ── Static sections ─────────────────────────────────────────
const Band = styled.section` background: ${theme.color.paperAlt}; border-block: 1px solid ${theme.color.line}; `;
const Facilities = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; margin-top: 2.5rem;
  @media (max-width: 820px) { grid-template-columns: 1fr 1fr; } @media (max-width: 520px) { grid-template-columns: 1fr; }
  .f { border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; padding: 1.5rem; background: #fff; }
  .f h3 { font-weight: 500; font-size: 1.15rem; color: ${theme.color.accent}; }
  .f p { margin: 0.4rem 0 0; font-size: 0.9rem; color: ${theme.color.inkSoft}; }
`;
const Info = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; margin-top: 1rem;
  @media (max-width: 720px) { grid-template-columns: 1fr; gap: 2rem; }
  .row { padding: 0.8rem 0; border-bottom: 1px solid ${theme.color.line}; display: flex; justify-content: space-between; gap: 1rem; font-size: 0.96rem; }
  .row span:first-child { color: ${theme.color.inkSoft}; }
  .row span:last-child { font-weight: 500; text-align: right; }
  ul { margin: 0.6rem 0 0; padding-left: 1.1rem; color: ${theme.color.inkSoft}; }
  li { margin-bottom: 0.5rem; }
`;

const DEPTS = LIBRARY_DEPTS;
const FACILITIES = [
  { h: 'Reference & lending', p: 'Trade manuals, guides and reference books across all four departments.' },
  { h: 'Computer & internet', p: 'A computer area with internet access for research and online learning.' },
  { h: 'Printing & copying', p: 'Printing, photocopying and scanning for study materials and assignments.' },
  { h: 'Study space', p: 'Quiet individual study and group discussion areas.' },
  { h: 'Digital resources', p: 'PDF manuals and digital materials you can open and read on any device.' },
  { h: 'Past papers', p: 'Practice with TEVET past papers and assessment guides.' },
];

function Catalogue() {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('');
  const [view, setView] = useState('tiles');
  const [open, setOpen] = useState(null);
  const [items, setItems] = useState(BUILT_IN_LIBRARY);

  // Anything in the library table replaces the built-in list. fetchLibrary
  // returns null while the table is empty, so a fresh install still shows a
  // full catalogue instead of a blank page.
  useEffect(() => {
    let active = true;
    fetchLibrary()
      .then((rows) => { if (active && rows?.length) setItems(rows); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((r) => {
      if (dept && r.department !== dept) return false;
      if (s && !`${r.title} ${r.type} ${r.department}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [q, dept, items]);

  // Fall back to the first four if nobody has flagged anything as featured.
  const flagged = items.filter((r) => r.featured);
  const featured = flagged.length ? flagged : items.slice(0, 4);

  return (
    <>
      {/* Featured */}
      <Section style={{ paddingBottom: '2.5rem' }}>
        <span className="kicker">Featured</span>
        <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.6rem,3.5vw,2.3rem)', margin: '1rem 0 1.8rem' }}>Popular this term</h2>
        <Tiles style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
          {featured.map((r) => (
            <Tile key={r.slug || r.title} onClick={() => setOpen(r)}>
              <Cover className="cover" $bg={DEPT_COLOR[r.department] || DEPT_COLOR.General}>
                <span className="type">{r.type}</span>
                <span className="title">{r.title}</span>
                <span className="dept">{r.department} · MTI</span>
                <span className="open">Open PDF ↗</span>
              </Cover>
            </Tile>
          ))}
        </Tiles>
      </Section>

      {/* Full catalogue */}
      <Section style={{ paddingTop: '2rem' }}>
        <span className="kicker">Browse the collection</span>
        <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.6rem,3.5vw,2.3rem)', margin: '1rem 0 1.6rem' }}>Catalogue</h2>

        <Controls>
          <input placeholder="Search the catalogue…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Toggle role="group" aria-label="View mode">
            <button className={view === 'tiles' ? 'active' : ''} onClick={() => setView('tiles')}>▦ Tiles</button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>☰ List</button>
          </Toggle>
        </Controls>

        <Chips>
          <button className={dept === '' ? 'active' : ''} onClick={() => setDept('')}>All</button>
          {DEPTS.map((d) => (
            <button key={d} className={dept === d ? 'active' : ''} onClick={() => setDept(d)}>{d}</button>
          ))}
        </Chips>

        <Count>{filtered.length} {filtered.length === 1 ? 'title' : 'titles'}</Count>

        {filtered.length === 0 ? (
          <Empty>No items match your search.</Empty>
        ) : view === 'tiles' ? (
          <Tiles>
            {filtered.map((r) => (
              <Tile key={r.slug || r.title} onClick={() => setOpen(r)}>
                <Cover className="cover" $bg={DEPT_COLOR[r.department] || DEPT_COLOR.General}>
                  <span className="type">{r.type}</span>
                  <span className="title">{r.title}</span>
                  <span className="dept">{r.department} · MTI</span>
                  <span className="open">Open PDF ↗</span>
                </Cover>
                <div className="meta">PDF · tap to open</div>
              </Tile>
            ))}
          </Tiles>
        ) : (
          <Rows>
            {filtered.map((r) => (
              <Row key={r.slug || r.title} onClick={() => setOpen(r)}>
                <Cover className="mini" $bg={DEPT_COLOR[r.department] || DEPT_COLOR.General}>
                  <span className="i">{(r.department || 'G')[0]}</span>
                </Cover>
                <span className="type">{r.type}</span>
                <span className="t">{r.title}</span>
                <span className="dept">{r.department}</span>
              </Row>
            ))}
          </Rows>
        )}
      </Section>

      <Band>
        <Section>
          <span className="kicker">What's available</span>
          <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.6rem,3.5vw,2.3rem)', marginTop: '1rem' }}>Facilities & services</h2>
          <Facilities>
            {FACILITIES.map((f) => <div className="f" key={f.h}><h3>{f.h}</h3><p>{f.p}</p></div>)}
          </Facilities>
        </Section>
      </Band>

      <Section>
        <Info>
          <div>
            <span className="kicker">Opening hours</span>
            <div style={{ marginTop: '1rem' }}>
              <div className="row"><span>Monday – Friday</span><span>08:00 – 17:00</span></div>
              <div className="row"><span>Saturday</span><span>08:00 – 13:00</span></div>
              <div className="row"><span>Sunday & holidays</span><span>Closed</span></div>
            </div>
            <p style={{ fontSize: '0.82rem', color: theme.color.inkSoft, marginTop: '0.8rem' }}>
              Hours are indicative — please confirm with the front desk.
            </p>
          </div>
          <div>
            <span className="kicker">Membership & borrowing</span>
            <ul>
              <li>Open to all enrolled MTI students and staff.</li>
              <li>Bring your student ID to register and borrow.</li>
              <li>Borrow up to 2 items at a time for 7 days, renewable.</li>
              <li>Reference and past-paper items are for use in the library only.</li>
            </ul>
            <Link to="/contact" className="kicker" style={{ marginTop: '1.4rem' }}>Ask the librarian →</Link>
          </div>
        </Info>
      </Section>

      <Viewer item={open} onClose={() => setOpen(null)} />
    </>
  );
}

function Library() {
  const { user, ready, signOut } = useAuth();

  return (
    <>
      <Navbar />
      <Header>
        <div className="inner">
          <span className="kicker">Learning resource centre</span>
          <h1>Library</h1>
          <p>Books, trade manuals, computers and study space to support your training — and your enterprise after you graduate.</p>
          {ready && user && (
            <p style={{ marginTop: '1.4rem', fontSize: '0.88rem' }}>
              Signed in as {user.email} ·{' '}
              <button onClick={signOut} style={{ background: 'none', border: 'none', color: theme.color.lemon, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.88rem' }}>
                Sign out
              </button>
            </p>
          )}
        </div>
      </Header>

      <Catalogue />

      <Footer />
    </>
  );
}

export default Library;
